import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { Box, LoadingOverlay, Stack, Table, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import {
	createFacetedMinMaxValues,
	createFacetedRowModel,
	createFacetedUniqueValues,
	createFilteredRowModel,
	createPaginatedRowModel,
	flexRender,
	useTable,
} from '@tanstack/react-table';
import log from 'loglevel';
import { useCallback, useEffect, useState } from 'react';

import { AddIcon, DragIcon } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { createDataTableFeatures } from '@apple/ui/data-table/features';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { createDataTableColumnHelper } from '@apple/ui/data-table/utils/column-helper';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { FormattedDate } from '@apple/utils/globalization/components/FormattedDate';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { FaqListItemAnswer } from '@apple/features/faq/components/FaqListItemAnswer';
import { updateFaqRank } from '@apple/features/faq/management';
import type { Faq, FaqFilters } from '@apple/features/faq/management.models';
import { faqManagementListQueryOptions } from '@apple/features/faq/management.queries';

const features = createDataTableFeatures({
	filteredRowModel: createFilteredRowModel(),
	paginatedRowModel: createPaginatedRowModel(),
	facetedRowModel: createFacetedRowModel(),
	facetedUniqueValues: createFacetedUniqueValues(),
	facetedMinMaxValues: createFacetedMinMaxValues(),
});

const defaultFilters: FaqFilters = {};
const defaultSorting: SortingState = [{ id: 'rank', desc: false }];

export const Route = createFileRoute('/_authed/_admin/content/faq')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ContentManagement',
		});
	},
	component: FAQManagePageRoute,
});

function FAQManagePageRoute() {
	const { t } = useTranslation('content');
	const navigate = Route.useNavigate();
	const location = useCurrentLocation();
	const search = Route.useSearch();
	const faqQuery = useQuery(faqManagementListQueryOptions);
	const [rows, setRows] = useState<Faq[]>([]);
	const [isUpdating, setIsUpdating] = useState(false);
	const columnHelper = createDataTableColumnHelper<Faq, FaqFilters>();

	useEffect(() => {
		if (faqQuery.data) setRows(faqQuery.data);
	}, [faqQuery.data]);

	const handleDragEnd = useCallback(
		async (result: DropResult) => {
			const { source, destination } = result;
			if (!destination || source.index === destination.index) return;

			const updated = Array.from(rows);
			const [moved] = updated.splice(source.index, 1);
			updated.splice(destination.index, 0, moved!);

			const reNumbered = updated.map((r, idx) => ({
				...r,
				rank: idx + 1,
			}));

			setRows(reNumbered);

			try {
				setIsUpdating(true);
				for (const row of reNumbered) {
					if (row.id && row.rank !== rows.find(r => r.id === row.id)?.rank) {
						await updateFaqRank(row.id, row.rank);
					}
				}
			} catch (err) {
				log.error('Error updating faq sort order:', err);
			} finally {
				setIsUpdating(false);
			}
		},
		[rows],
	);

	const tableState = useTableState<Faq, FaqFilters>({
		search,
		navigate,
		defaultFilters: defaultFilters,
		defaultSorting: defaultSorting,
		fieldMap: [],
	});

	const columns = columnHelper.columns([
		columnHelper.accessor('question', {
			header: t('faqs.headers.question'),
			size: 300,
			filter: { group: t('projects.filter-groups.details.title') },
			cell: info => (
				<Link
					to='/content/faq/$faqId'
					state={{ from: location }}
					params={{ faqId: info.row.original.id! }}
					children={info.getValue()}
				/>
			),
		}),
		columnHelper.accessor('answer', {
			header: t('faqs.headers.answer'),
			filter: { group: t('projects.filter-groups.details.title') },
			cell: info => <FaqListItemAnswer answer={info.getValue()} />,
		}),
		columnHelper.accessor('active', {
			header: t('faqs.headers.status'),
			size: 50,
			filter: {
				label: t('faqs.labels.active'),
				group: t('projects.filter-groups.details.title'),
				variant: 'switch',
				getFilterDisplayValue: v =>
					v ? t('faqs.labels.active') : t('faqs.labels.inactive'),
			},
			cell: info => (
				<Text>{info.getValue() ? t('faqs.labels.active') : t('faqs.labels.inactive')}</Text>
			),
		}),
		columnHelper.accessor('createdDate', {
			header: t('faqs.headers.createdDate'),
			size: 200,
			cell: info => <FormattedDate value={info.getValue()} />,
			enableColumnFilter: false,
		}),
		columnHelper.filterOnly('rank', {
			label: t('faqs.headers.rank'),
			group: t('projects.filter-groups.details.title'),
			variant: 'number-range-slider',
		}),
	]);

	const table = useTable({
		features,
		data: rows,
		...tableState,
		enableFilters: true,
		enableColumnFilters: true,
		enableSorting: false,
		columnResizeMode: 'onChange',
		columns: columns,
	});

	return (
		<TableLayout
			table={table}
			title={t('faqs.title')}
			toolbarButtons={[
				<ToolbarButton
					key='add'
					tooltip={t('faqs.add.title')}
					icon={AddIcon}
					onClick={() =>
						void navigate({
							to: '/content/faq/add',
							state: { from: location },
							viewTransition: false,
						})
					}
				/>,
			]}
			stackJustify='flex-start'
		>
			{!faqQuery.isFetching && rows.length > 0 && (
				<Stack>
					<Text size='sm' c='dimmed' pt='xs' px='xs'>
						{t('faqs.description')}
					</Text>
					<Box pos='relative'>
						<LoadingOverlay visible={isUpdating} overlayProps={{ blur: 2 }} />

						<DragDropContext onDragEnd={handleDragEnd}>
							<Table variant='apple-table'>
								<Table.Thead>
									{table.getHeaderGroups().map(headerGroup => (
										<Table.Tr key={headerGroup.id}>
											<Table.Th />
											{headerGroup.headers
												.filter(x => x.column.getSize() > 0)
												.map(header => (
													<Table.Th
														key={header.id}
														style={{ whiteSpace: 'nowrap' }}
													>
														{flexRender(
															header.column.columnDef.header,
															header.getContext(),
														)}
													</Table.Th>
												))}
										</Table.Tr>
									))}
								</Table.Thead>

								<Droppable droppableId='faq-body'>
									{provided => (
										<Table.Tbody
											ref={provided.innerRef}
											{...provided.droppableProps}
										>
											{table.getRowModel().rows.map((row, idx) => (
												<Draggable
													key={row.original.id}
													draggableId={String(row.original.id)}
													index={idx}
												>
													{dragProvided => (
														<Table.Tr
															ref={dragProvided.innerRef}
															{...dragProvided.draggableProps}
															{...dragProvided.dragHandleProps}
														>
															<Table.Td>
																<DragIcon c='dimmed' />
															</Table.Td>
															{row.getVisibleCells().map(cell => (
																<Table.Td key={cell.id}>
																	{flexRender(
																		cell.column.columnDef.cell,
																		cell.getContext(),
																	)}
																</Table.Td>
															))}
														</Table.Tr>
													)}
												</Draggable>
											))}
											{provided.placeholder}
										</Table.Tbody>
									)}
								</Droppable>
							</Table>
						</DragDropContext>
					</Box>
				</Stack>
			)}
		</TableLayout>
	);
}
